import React, { useEffect, useState, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';
import { useToasts } from 'react-toast-notifications';

import { setWalletBalance } from '../../../actions/wallet';
import FundsTransferReducer, { initialFormState } from './transfer-reducer';
import { setCurrentPage } from '../../../actions/page';
import { DISBURSE_FUNDS, TRANSACTION_COST as TRANSACTION_COST_URL } from '../../../utils/constants';
import FundsTransferForm from './FundsTransferForm';
import FundsTransferCompleted from './FundsTransferCompleted';
import FundsTransferSummary from './FundsTransferSummary';
import FailedTransaction from '../../../components/common/FailedTransaction';
import { EventEmitter } from '../../../utils/event';

import styles from './index.module.scss';

export const FundsTransfer = ({ changeCurrentPage, hasSetPin }) => {
    const REQUEST_TIMEOUT = 100000; 
    
    let renderedComponent;
    const [componentToRender, setComponentToRender] = useState('form');
    const [FundsTransferFormState, dispatch] = useReducer(
        FundsTransferReducer,
        initialFormState
    );
    const [successData, setSuccessData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [transactionCost, setTransactionCost] = useState(null);
    const [agentLocation, setAgentLocation] = useState(null);
    const [failedErrorMessage, setFailedErrorMessage] = useState('');
    const { addToast } = useToasts();
    
    const isMountedRef = useRef(true);
    const currentRequestRef = useRef(null);

    // 🔹 Fetch transaction cost dynamically
    useEffect(() => {
        let isCancelled = false;
        setLoading(true);

        axios.get(TRANSACTION_COST_URL)
            .then((res) => {
                if (!isCancelled && isMountedRef.current) {
                    setTransactionCost(res.data?.data?.cost || 0);
                }
            })
            .catch(() => {
                if (!isCancelled && isMountedRef.current) {
                    setTransactionCost(0); // fallback if API fails
                }
            })
            .finally(() => {
                if (!isCancelled && isMountedRef.current) {
                    setLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(function (position) {
                if (isMountedRef.current) {
                    setAgentLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    });
                }
            });
        }
        
        return () => {
            isMountedRef.current = false;
            if (currentRequestRef.current) {
                currentRequestRef.current.cancel('Component unmounted');
            }
        };
    }, []);

    useEffect(() => {
        changeCurrentPage({
            heading: 'Funds Transfer',
            search: false,
        });
    }, []);

    const handleOnSubmit = async () => {
        if (loading) return;
        setLoading(true);
        
        const {
            accountNumber,
            beneficiaryBankCode,
            amount,
            transaction_pin,
        } = FundsTransferFormState;

        const req = {
            account_number: accountNumber,
            bank: beneficiaryBankCode,
            amount: amount,
            transaction_pin,
        };

        try {
            const cancelToken = axios.CancelToken.source();
            currentRequestRef.current = cancelToken;

            const options = {
                headers: {
                    lat: agentLocation?.latitude,
                    lng: agentLocation?.longitude,
                },
                timeout: REQUEST_TIMEOUT,
                cancelToken: cancelToken.token
            };

            const res = await axios.post(DISBURSE_FUNDS, req, options);
            
            if (!isMountedRef.current) return;

            const reference = res.data?.data?.Data?.TxnId;
            const status = res.data.status;
            const message = res.data.data.Message;
            const agentCode = res.data.data.agent_code;
            const transactionDate = res.data.data.transaction_date;
            
            setSuccessData({
                message,
                reference,
                status,
                agentCode,
                transactionCost,
                date: transactionDate,
            });

            setLoading(false);
            addToast(message, {
                appearance: 'success',
                autoDismiss: true,
            });
            setComponentToRender('completed');
            EventEmitter.dispatch('refresh-wallet-balance', {});
            
        } catch (err) {
            if (!isMountedRef.current || axios.isCancel(err)) return;
            
            console.log(err);
            setLoading(false);
            
            if (!err.response) {
                addToast("Unable to process your request. Please check your connection and try again.", {
                    appearance: 'error',
                    autoDismiss: true,
                });
                setFailedErrorMessage("Network error or request timeout");
                setComponentToRender('failed');
                return;
            }

            const errorMessage = err.response.data?.message || 'An error occurred';
            
            addToast(errorMessage, {
                appearance: 'error',
                autoDismiss: true,
            });
            
            setFailedErrorMessage(errorMessage);
            setComponentToRender('failed');
            
        } finally {
            if (isMountedRef.current) {
                currentRequestRef.current = null;
                setLoading(false);
            }
        }
    };

    switch (componentToRender) {
        case 'form':
            renderedComponent = (
                <FundsTransferForm
                    FundsTransferFormState={FundsTransferFormState}
                    dispatch={dispatch}
                    setComponentToRender={setComponentToRender}
                    transactionCost={transactionCost}
                />
            );
            break;
        case 'summary':
            renderedComponent = (
                <FundsTransferSummary
                    loading={loading}
                    setLoading={setLoading}
                    dispatch={dispatch}
                    handleOnSubmit={handleOnSubmit}
                    transactionCost={transactionCost}
                    setComponentToRender={setComponentToRender}
                    FundsTransferFormState={FundsTransferFormState}
                    hasSetPin={hasSetPin}
                />
            );
            break;
        case 'completed':
            renderedComponent = (
                <FundsTransferCompleted
                    successData={successData}
                    setComponentToRender={setComponentToRender}
                    FundsTransferFormState={FundsTransferFormState}
                />
            );
            break;
        case 'failed':
            renderedComponent = (
                <FailedTransaction message={failedErrorMessage} />
            );
            break;
        default:
            renderedComponent = null;
    }

    return <div>{renderedComponent}</div>;
};

const mapStateToProps = (state) => {
    return {
        hasSetPin: state.auth.user.hasSetPin,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        changeCurrentPage: (payload) => dispatch(setCurrentPage(payload)),
        setWalletBalance: (payload) => dispatch(setWalletBalance(payload)),
    };
};

FundsTransfer.propTypes = {
    changeCurrentPage: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(FundsTransfer);
